import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.tsx';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { useCustomers } from '../hooks/useCustomers.ts';
import { createLogger } from '@metron/shared';
import { copyToClipboard } from '../lib/clipboard.ts';

const logger = createLogger('CreateDataLinkAccountModal');

interface DataLinkOnboardingRequest {
  email: string;
  customerId: number;
}

interface DataLinkOnboardingResponse {
  username: string;
  temporaryPassword: string;
}

interface CreateDataLinkAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateAccount: (
    data: DataLinkOnboardingRequest,
  ) => Promise<DataLinkOnboardingResponse>;
}

export function CreateDataLinkAccountModal({
  open,
  onOpenChange,
  onCreateAccount,
}: CreateDataLinkAccountModalProps) {
  const { customers, isLoading: isLoadingCustomers } = useCustomers();

  const [formData, setFormData] = useState({
    email: '',
    customerId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingResult, setOnboardingResult] = useState<
    (DataLinkOnboardingResponse & { customerName: string }) | null
  >(null);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.customerId) {
      newErrors.customerId = 'Customer selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsLoading(true);
    try {
      const response = await onCreateAccount({
        email: formData.email,
        customerId: parseInt(formData.customerId, 10),
      });

      if (response) {
        // Find the customer name for display
        const customer = customers.find((c) => c.id === parseInt(formData.customerId, 10));
        const customerName = customer?.waterscopeAccountName || 'Customer';

        // Show the result with credentials
        setOnboardingResult({
          username: response.username,
          temporaryPassword: response.temporaryPassword,
          customerName,
        });

        toast.success('DataLink account created successfully');
      }
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to create DataLink account';
      toast.error(errorMessage);
      logger.error({ error }, 'Failed to create DataLink account');
    } finally {
      setIsLoading(false);
    }
  };

  const emailTemplate = onboardingResult
    ? `Subject: Your DataLink API Access Credentials

Hello,

Your DataLink API access has been configured. Below are your credentials:

Username: ${onboardingResult.username}
Temporary Password: ${onboardingResult.temporaryPassword}

IMPORTANT: You must change your password on first login.

To change your password:
curl -X POST "https://api.waterscope.us/v1/auth/change-password" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "${onboardingResult.username}", "temporaryPassword": "${onboardingResult.temporaryPassword}", "newPassword": "<your_new_password>"}'

To generate access tokens for API access:
curl -X POST "https://api.waterscope.us/v1/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "${onboardingResult.username}", "password": "<your_password>"}'

If you have any questions, please contact support.

Best regards,
Metron Team`
    : '';

  const handleClose = () => {
    setFormData({
      email: '',
      customerId: '',
    });
    setErrors({});
    setOnboardingResult(null);
    onOpenChange(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px] max-h-[90vh] overflow-y-auto'>
        {!onboardingResult
          ? (
            <>
              <DialogHeader>
                <DialogTitle>Create DataLink Account</DialogTitle>
                <DialogDescription>
                  Create a new DataLink API customer. This will generate credentials for
                  programmatic access to the DataLink API. All fields are required.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className='grid gap-4 py-4'>
                  <div className='grid gap-2'>
                    <Label htmlFor='email'>
                      Email Address <span className='text-destructive'>*</span>
                    </Label>
                    <Input
                      id='email'
                      type='email'
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder='user@example.com'
                      className={errors.email ? 'border-destructive' : ''}
                      disabled={isLoading}
                      required
                    />
                    {errors.email && <p className='text-sm text-destructive'>{errors.email}</p>}
                    <p className='text-sm text-muted-foreground'>
                      This email will be used as the username for API authentication
                    </p>
                  </div>

                  <div className='grid gap-2'>
                    <Label htmlFor='customerId'>
                      Customer <span className='text-destructive'>*</span>
                    </Label>
                    <Select
                      value={formData.customerId}
                      onValueChange={(value) => handleInputChange('customerId', value)}
                      disabled={isLoadingCustomers || isLoading}
                    >
                      <SelectTrigger
                        id='customerId'
                        className={errors.customerId ? 'border-destructive' : ''}
                      >
                        <SelectValue placeholder='Select a customer' />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id.toString()}>
                            {customer.waterscopeAccountName} ({customer.waterscopeAccountId})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.customerId && (
                      <p className='text-sm text-destructive'>{errors.customerId}</p>
                    )}
                    {isLoadingCustomers && (
                      <p className='text-sm text-muted-foreground'>Loading customers...</p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button type='submit' disabled={isLoading || isLoadingCustomers}>
                    {isLoading ? 'Creating...' : 'Create Account'}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )
          : (
            <>
              <DialogHeader>
                <DialogTitle>Account Created Successfully</DialogTitle>
                <DialogDescription>
                  Copy the information below and send it to the customer via email.
                </DialogDescription>
              </DialogHeader>
              <div className='grid gap-4 py-4'>
                <div className='grid gap-2'>
                  <Label>Customer Name</Label>
                  <div className='p-3 bg-muted rounded-md font-mono text-sm'>
                    {onboardingResult.customerName}
                  </div>
                </div>

                <div className='grid gap-2'>
                  <Label>Username (Email)</Label>
                  <div className='flex items-center gap-2'>
                    <div className='flex-1 p-3 bg-muted rounded-md font-mono text-sm break-all'>
                      {onboardingResult.username}
                    </div>
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      aria-label='Copy username'
                      onClick={() => copyToClipboard(onboardingResult.username, 'Username')}
                    >
                      <Copy className='h-4 w-4' />
                    </Button>
                  </div>
                </div>

                <div className='grid gap-2'>
                  <Label>Temporary Password</Label>
                  <div className='flex items-center gap-2'>
                    <div className='flex-1 p-3 bg-muted rounded-md font-mono text-sm break-all'>
                      {onboardingResult.temporaryPassword}
                    </div>
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      aria-label='Copy temporary password'
                      onClick={() =>
                        copyToClipboard(onboardingResult.temporaryPassword, 'Temporary password')}
                    >
                      <Copy className='h-4 w-4' />
                    </Button>
                  </div>
                </div>

                <div className='grid gap-2'>
                  <div className='flex items-center justify-between'>
                    <Label>Email Template</Label>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      aria-label='Copy email template'
                      onClick={() => copyToClipboard(emailTemplate, 'Email template')}
                    >
                      <Copy className='mr-2 h-4 w-4' />
                      Copy
                    </Button>
                  </div>
                  <div className='p-3 bg-muted rounded-md text-sm whitespace-pre-wrap font-mono'>
                    {emailTemplate}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleClose}>Done</Button>
              </DialogFooter>
            </>
          )}
      </DialogContent>
    </Dialog>
  );
}
